import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { getContentWithLineWraps, getEventTime, getTagsFromEvent, isAddedToday } from './utils'
import { translate as _ } from '../../services/translations'
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, Tooltip } from '@mui/material'
import './event.scss'
import classNames from 'classnames'
import { EventTag } from './event-tag'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash'
import { useDispatch } from 'react-redux'
import { updateEventModalId } from '../../services/redux-reducers/application/application-reducer'
import { TOAST_SEVERITY } from '../toast'
import { isSessionExpiredResponse } from '../../services/session'

export const Event = ({
  data,
  isOpened,
  onClick,
  withDate,
  showEditors,
  exportingMode,
  onExport,
  isExported,
  editMode,
  onEditClick,
  onRestore,
  onToast
}) => {
  const [checked, setChecked] = useState(isExported)
  const {
    start_time: startTime,
    content,
    title,
    id,
    no_time: noTime,
    tags2,
    added,
    editors,
    deleted,
    link
  } = data

  const dispatch = useDispatch()
  const wrappedContent = getContentWithLineWraps(content)

  useEffect(() => {
    setChecked(isExported)
  })

  const softRemoveTooltip = _(['eventEditor', 'softRemoveTooltip'])
  const hardRemoveTooltip = _(['eventEditor', 'hardRemoveTooltip'])
  const restoreTooltip = _(['eventEditor', 'restoreTooltip'])
  const restoreSuccessMessage = _(['events', 'restoreSuccessMessage'])
  const restoreErrorMessage = _(['events', 'restoreErrorMessage'])
  const sessionExpiredMessage = _(['session', 'expiredAction'])

  const handleOnClick = (newState) => {
    onClick(id, newState)
  }

  const handleOnExportClick = () => {
    const newValue = !checked
    setChecked(newValue)
    onExport(id, newValue)
  }

  const handleOnEditClick = () => {
    onEditClick(id)
  }

  const handleOnRestoreClick = () => {
    const URL = 'https://kalendarium.tasr.sk/public/index.php/api/events/restore'

    fetch(URL, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ id }),
      mode: 'cors',
      headers: { 'content-type': 'application/json; charset=UTF-8' }
    })
      .then((result) => result.json())
      .then((response) => {
        if (isSessionExpiredResponse(response)) {
          onToast(sessionExpiredMessage, TOAST_SEVERITY.DANGER)
          return
        }

        onToast(restoreSuccessMessage, TOAST_SEVERITY.SUCCESS)
        onRestore(id)
      })
      .catch(() => {
        onToast(restoreErrorMessage, TOAST_SEVERITY.DANGER)
      })
  }

  const handleOnSoftDeleteClick = () => {
    if (!deleted) {
      dispatch(updateEventModalId({ id, deletePower: 1 }))
    }
  }

  const handleOnHardDeleteClick = () => {
    if (!deleted) {
      dispatch(updateEventModalId({ id, deletePower: 2 }))
    }
  }

  const handleOnTagClick = (variant) => {
    if (variant === 'link') {
      window.open(link, '_blank')
    }
  }

  const renderTags = () => {
    const tags = getTagsFromEvent({ tags: tags2, editors: showEditors ? editors : null, link })

    return tags.map((tag, idx) => (
      <EventTag
        variant={tag.variant}
        title={tag.title}
        key={`event-tag-${id}-${tag}-${idx}`}
        onClick={tag.variant === 'link' ? handleOnTagClick : undefined}
        id={`${id}`}
      />
    ))
  }

  const renderEditMode = () => {
    return (
      <div className="event-icons">
        <div className="event-icon">
          <ModeEditIcon onClick={handleOnEditClick} id={`edit-mode-button-${id}`} />
        </div>
        {!deleted && (
          <div className="event-icon">
            <Tooltip title={softRemoveTooltip} arrow placement="top">
              <span>
                <DeleteIcon onClick={handleOnSoftDeleteClick} id={`soft-delete-button-${id}`} />
              </span>
            </Tooltip>
          </div>
        )}
        {deleted && (
          <div className="event-icon restore">
            <Tooltip title={restoreTooltip} arrow placement="top">
              <span>
                <RestoreFromTrashIcon onClick={handleOnRestoreClick} id={`restore-button-${id}`} />
              </span>
            </Tooltip>
          </div>
        )}
        <div className={`event-icon hard-remove${deleted ? ' disabled' : ''}`}>
          <Tooltip title={deleted ? '' : hardRemoveTooltip} arrow placement="top">
            <span>
              <DeleteForeverIcon
                onClick={handleOnHardDeleteClick}
                id={`hard-delete-button-${id}`}
              />
            </span>
          </Tooltip>
        </div>
      </div>
    )
  }

  return (
    <div className="event-wrapper">
      <div className="event-export-control">
        {exportingMode && (
          <Checkbox
            checked={checked}
            onChange={handleOnExportClick}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        )}
        {editMode && renderEditMode()}
      </div>
      <div className="event-accordion">
        <Accordion onChange={(e, newState) => handleOnClick(newState)} expanded={isOpened}>
          <AccordionSummary>
            <div className="event-summary-wrapper">
              <div className="event-tags">{renderTags()}</div>
              <div className="event-text-wrapper" key={`event-${id}`} onClick={handleOnClick}>
                <div className={classNames('event-time', { 'no-time': noTime && !withDate })}>
                  {getEventTime(startTime, noTime, withDate)}
                </div>
                <div className="event-title">{title}</div>
                {isAddedToday(startTime, added) && (
                  <div className="added-today">{_(['events', 'addedToday'])}</div>
                )}
                {deleted && isAddedToday(startTime, deleted) && (
                  <div className="deleted-today">{_(['events', 'deletedToday'])}</div>
                )}
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className="event-content">
              {wrappedContent.map((line, idx) => (
                <span className="content-line" key={`content-line-${id}-${idx}`}>
                  {line}
                </span>
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  )
}

Event.propTypes = {
  data: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  withDate: PropTypes.bool.isRequired,
  showEditors: PropTypes.bool.isRequired,
  exportingMode: PropTypes.bool.isRequired,
  onExport: PropTypes.func.isRequired,
  isExported: PropTypes.bool.isRequired,
  editMode: PropTypes.bool.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  onToast: PropTypes.func.isRequired
}
